import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { MdFeedback } from 'react-icons/md';
import { FiChevronDown, FiEdit } from 'react-icons/fi';
import { CiSettings } from "react-icons/ci";
import { IoMdPrint, IoIosLogOut } from "react-icons/io";
import { LuSettings2 } from "react-icons/lu";
import { FaTrash } from 'react-icons/fa';
import SettingsPopup from './SettingsPopup';
import CreateExpensePopup from './CreateExpensePopup';
import axios from 'axios';
import './ExpensesPage.css';

// Date utility functions
const getCurrentDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}-${month}-${year}`;
};

const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return getCurrentDate();

    // If already in DD-MM-YYYY format, return as is
    if (typeof dateString === 'string' && dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2) {
            return dateString;
        }
    }

    // Parse the date and format to DD-MM-YYYY
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return getCurrentDate();

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
};

const parseDDMMYYYYToDate = (dateString) => {
    if (!dateString) return new Date();

    if (dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            return new Date(year, month, day);
        }
    }

    return new Date(dateString);
};

const ExpensesPage = () => {
    const [showAddDropdown, setShowAddDropdown] = useState(false);
    const [showPayDropdown, setShowPayDropdown] = useState(false);
    const [showCreateExpensePopup, setShowCreateExpensePopup] = useState(false);
    const [showSettingsPopup, setShowSettingsPopup] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    const [loadingExpenses, setLoadingExpenses] = useState(false);
    const [error, setError] = useState(null);
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [supplierFilter, setSupplierFilter] = useState("All");
    const [expenseDateFilter, setExpenseDateFilter] = useState("All Dates");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [filteredExpenses, setFilteredExpenses] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [settings, setSettings] = useState({
        showAllTransactions: false,
        showDate: true,
        showType: true,
        showNumber: true,
        showPayee: true,
        showCategory: true,
        showTotal: true,
        showAction: true,
        showClass: false,
        showLocation: false,
        showStatus: false,
        showMethod: false,
        showSource: false,
        showMemo: false,
        showDueDate: false,
        showBalance: false,
        showTotalBalanceSalesTax: false,
        showSalesTax: false,
        showAttachments: false,
        showBillApproval: false,
        rowLimit: 8,
    });
    const [selectedRows, setSelectedRows] = useState([]);
    const [tableData, setTableData] = useState([
        {
            id: Date.now(),
            rowNumber: "1",
            category: "",
            description: "",
            totalAmount: "",
        },
    ]);
    const [expenseFile, setExpenseFile] = useState(null);
    const settingsButtonRef = useRef(null);
    const addDropdownRef = useRef(null);
    const payDropdownRef = useRef(null);
    const settingsPopupRef = useRef(null);
    const filterPopupRef = useRef(null);
    const filterButtonRef = useRef(null);
    const [paymentAccount, setPaymentAccount] = useState("Cash on hand");
    const [payee, setPayee] = useState('');
    const [paymentDate, setPaymentDate] = useState(getCurrentDate());
    const [paymentMethod, setPaymentMethod] = useState('');
    const [totalAmount, setTotalAmount] = useState(0);
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [categoryMap, setCategoryMap] = useState({});

    const API_BASE_URL = "https://backend.hira-tech.co.uk";

    useEffect(() => {
        setLoadingSuppliers(true);
        fetch(`${API_BASE_URL}/api/categories/`, {
            headers: { Accept: "application/json" },
        })
            .then((response) => {
                if (!response.ok) throw new Error(`Categories fetch error! Status: ${response.status}`);
                return response.json();
            })
            .then((data) => {
                setCategories(data);
                const map = data.reduce((acc, cat) => {
                    acc[cat.name] = cat.display_name;
                    return acc;
                }, {});
                setCategoryMap(map);
                setError(null);
            })
            .catch((error) => {
                console.error(`Failed to fetch categories: ${error.message}`);
                setError(`Failed to fetch categories: ${error.message}`);
            })
            .finally(() => setLoadingSuppliers(false));
    }, []);

    useEffect(() => {
        const calculatedTotal = tableData.reduce((sum, row) => sum + (parseFloat(row.totalAmount) || 0), 0);
        setTotalAmount(calculatedTotal);
    }, [tableData]);

    useEffect(() => {
        const fetchSuppliers = async () => {
            setLoadingSuppliers(true);
            try {
                const response = await axios.get(`${API_BASE_URL}/api/suppliers/`, {
                    headers: {
                        'Accept': 'application/json',
                    },
                });
                const mappedSuppliers = response.data.map(supplier => ({
                    id: Number(supplier.id),
                    supplier_display_name: supplier.supplier_display_name || `${supplier.first_name || ''} ${supplier.last_name || ''}`.trim() || 'Unknown Supplier',
                }));
                console.log("Fetched suppliers:", mappedSuppliers);
                setSuppliers(mappedSuppliers);
                setError(null);
            } catch (error) {
                console.error("Error fetching suppliers:", error.response ? error.response.data : error.message);
                setError(`Failed to fetch suppliers: ${error.response ? JSON.stringify(error.response.data, null, 2) : error.message}`);
            } finally {
                setLoadingSuppliers(false);
            }
        };
        fetchSuppliers();
    }, []);

    useEffect(() => {
        const fetchExpenses = async () => {
            setLoadingExpenses(true);
            try {
                const response = await axios.get(`${API_BASE_URL}/api/expenses/`, {
                    headers: {
                        'Accept': 'application/json',
                    },
                });
                console.log("Fetched expenses:", response.data);
                const mappedExpenses = response.data
                    .filter(expense => {
                        if (isNaN(expense.id)) {
                            console.error("Invalid expense ID from API:", expense.id);
                            return false;
                        }
                        return true;
                    })
                    .map(expense => {
                        const itemsTotal = expense.items ? expense.items.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0) : 0;
                        const total = parseFloat(expense.total?.replace('£', '')) || itemsTotal || 0;
                        const displayCategory = categoryMap[expense.category] || expense.category || "General";
                        return {
                            id: Number(expense.id),
                            date: formatDateToDDMMYYYY(expense.date) || getCurrentDate(),
                            type: expense.type || "Expense",
                            number: expense.no || expense.expense_number || "No Number",
                            payee: expense.supplier || expense.payee || "Unknown",
                            category: displayCategory,
                            memo: expense.memo || "",
                            balance: expense.totalBeforeTax || `£${itemsTotal.toFixed(2)}`,
                            totalBalanceSalesTax: expense.total || `£${total.toFixed(2)}`,
                            salesTax: expense.salesTax || "£0.00",
                            total: `£${total.toFixed(2)}`,
                            attachments: expense.file_path ? expense.file_path.split('/').pop() : "None",
                            action: "View/Edit",
                            address: expense.address || "No address",
                            items: expense.items ? expense.items.map(item => ({
                                id: Date.now() + Math.random(),
                                rowNumber: item.rowNumber || "1",
                                category: categoryMap[item.category] || item.category || "",
                                description: item.description || "",
                                totalAmount: item.totalAmount ? String(item.totalAmount) : "",
                            })) : [
                                {
                                    id: Date.now(),
                                    rowNumber: "1",
                                    category: displayCategory,
                                    description: expense.memo || "",
                                    totalAmount: total ? String(total) : "",
                                },
                            ],
                            file_path: expense.file_path || "",
                            file_name: expense.file_path ? expense.file_path.split('/').pop() : "None",
                            class: expense.class || `Class-${Math.floor(Math.random() * 100)}`,
                            location: expense.location || `Location-${Math.floor(Math.random() * 100)}`,
                            status: "Paid",
                            method: expense.method || ["Cash", "Card", "Bank Transfer"][Math.floor(Math.random() * 3)],
                            source: expense.source || ["Manual", "Imported"][Math.floor(Math.random() * 2)],
                            dueDate: formatDateToDDMMYYYY(expense.dueDate) || getCurrentDate(),
                            billApproval: expense.billApproval || ["Yes", "No"][Math.floor(Math.random() * 2)],
                        };
                    });
                setExpenses(mappedExpenses);
                setFilteredExpenses(mappedExpenses);
                setError(null);
            } catch (error) {
                console.error("Error fetching expenses:", error.response ? error.response.data : error.message);
                setError(`Failed to fetch expenses: ${error.response ? JSON.stringify(error.response.data, null, 2) : error.message}`);
            } finally {
                setLoadingExpenses(false);
            }
        };
        fetchExpenses();
    }, [categoryMap]);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (
                filterPopupRef.current &&
                !filterPopupRef.current.contains(e.target) &&
                filterButtonRef.current &&
                !filterButtonRef.current.contains(e.target)
            ) {
                setShowFilterPopup(false);
            }
            if (
                addDropdownRef.current &&
                !addDropdownRef.current.contains(e.target)
            ) {
                setShowAddDropdown(false);
            }
            if (
                payDropdownRef.current &&
                !payDropdownRef.current.contains(e.target)
            ) {
                setShowPayDropdown(false);
            }
            if (
                settingsPopupRef.current &&
                !settingsPopupRef.current.contains(e.target) &&
                settingsButtonRef.current &&
                !settingsButtonRef.current.contains(e.target)
            ) {
                setShowSettingsPopup(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    const handleCreateExpenseClick = () => {
        setSelectedExpense(null);
        setPayee('');
        setPaymentDate(getCurrentDate());
        setPaymentMethod('');
        setDescription('');
        setCategory('');
        setExpenseFile(null);
        setTableData([
            {
                id: Date.now(),
                rowNumber: "1",
                category: "",
                description: "",
                totalAmount: "",
            },
        ]);
        setShowCreateExpensePopup(true);
        setShowAddDropdown(false);
    };

    const handleViewEditExpense = (expense) => {
        console.log("Selected expense:", expense);
        if (isNaN(expense.id)) {
            console.error("Invalid expense ID:", expense.id);
            alert("Error: Invalid expense ID. Please try again.");
            return;
        }
        setSelectedExpense({
            id: Number(expense.id),
            date: formatDateToDDMMYYYY(expense.date) || getCurrentDate(),
            expense_number: expense.number,
            supplier: expense.supplier || expense.payee,
            address: expense.address || "",
            terms: expense.category,
            memo: expense.memo || "",
            status: "Paid",
            amount: parseFloat(expense.balance?.replace('£', '')) || 0,
            sales_tax: parseFloat(expense.salesTax?.replace('£', '')) || 0,
            total: parseFloat(expense.total?.replace('£', '')) || 0,
            file_path: expense.file_path,
            file_name: expense.file_name,
            items: expense.items,
            class: expense.class,
            location: expense.location,
            method: expense.method,
            source: expense.source,
            dueDate: formatDateToDDMMYYYY(expense.dueDate) || getCurrentDate(),
            billApproval: expense.billApproval,
        });
        setPayee(expense.payee || '');
        setPaymentDate(formatDateToDDMMYYYY(expense.date) || getCurrentDate());
        setPaymentMethod(expense.method || '');
        setDescription(expense.memo || '');
        setCategory(categoryMap[expense.category] || expense.category || '');
        setExpenseFile(expense.file_name ? { name: expense.file_name } : null);
        setTableData(expense.items ? expense.items.map(item => ({
            id: Date.now() + Math.random(),
            rowNumber: item.rowNumber || "1",
            category: categoryMap[item.category] || item.category || "",
            description: item.description || "",
            totalAmount: item.totalAmount ? String(item.totalAmount) : "",
        })) : [
            {
                id: Date.now(),
                rowNumber: "1",
                category: categoryMap[expense.category] || expense.category || "",
                description: expense.memo || "",
                totalAmount: expense.total ? String(parseFloat(expense.total.replace('£', ''))) : "",
            },
        ]);
        setShowCreateExpensePopup(true);
    };

    const handleDeleteExpense = async (expenseId, expenseNumber) => {
        if (!window.confirm(`Are you sure you want to delete expense ${expenseNumber}?`)) {
            return;
        }
        try {
            await axios.delete(`${API_BASE_URL}/api/expenses/${expenseId}/`, {
                headers: {
                    'Accept': 'application/json',
                },
            });
            setExpenses(prev => prev.filter(expense => expense.id !== expenseId));
            setFilteredExpenses(prev => prev.filter(expense => expense.id !== expenseId));
            setError(null);
            alert(`Expense ${expenseNumber} deleted successfully!`);
        } catch (error) {
            console.error("Error deleting expense:", error.response ? error.response.data : error.message);
            setError(`Failed to delete expense: ${error.response ? JSON.stringify(error.response.data, null, 2) : error.message}`);
        }
    };

    const handleRowClick = (e, expense) => {
        if (
            e.target.tagName.toLowerCase() === 'input' ||
            e.target.closest('.action-icon')
        ) {
            return;
        }
        handleViewEditExpense(expense);
    };

    const handleAddRow = () => {
        setTableData([
            ...tableData,
            {
                id: Date.now(),
                rowNumber: (tableData.length + 1).toString(),
                category: "",
                description: "",
                totalAmount: "",
            },
        ]);
    };

    const handleDeleteRow = (id) => {
        setTableData(
            tableData
                .filter((row) => row.id !== id)
                .map((row, index) => ({ ...row, rowNumber: (index + 1).toString() }))
        );
    };

    const handleCopyRow = (id) => {
        const rowToCopy = tableData.find((row) => row.id === id);
        if (rowToCopy) {
            setTableData([
                ...tableData,
                { ...rowToCopy, id: Date.now(), rowNumber: (tableData.length + 1).toString() },
            ]);
        }
    };

    const handleUpdateRow = (id, field, value) => {
        setTableData(
            tableData.map((row) =>
                row.id === id
                    ? {
                        ...row,
                        [field]: field === "totalAmount" ? (value === '' || /^\d*\.?\d*$/.test(value) ? value : row.totalAmount) : value,
                    }
                    : row
            )
        );
    };

    const handleClearAllLines = () => {
        setTableData([
            {
                id: Date.now(),
                rowNumber: "1",
                category: "",
                description: "",
                totalAmount: "",
            },
        ]);
    };

    const handleClear = () => {
        setPayee('');
        setPaymentDate(getCurrentDate());
        setPaymentMethod('');
        setDescription('');
        setCategory('');
        setExpenseFile(null);
        setTableData([
            {
                id: Date.now(),
                rowNumber: "1",
                category: "",
                description: "",
                totalAmount: "",
            },
        ]);
    };

    const handlePrint = () => {
        window.print();
    };

    const handleRecurring = () => {
        alert("Recurring expense creation is not implemented yet.");
    };

    const handleSaveAndNew = async (newExpense) => {
        try {
            console.log("Handle Save and New - newExpense:", newExpense);
            console.log("Manual expense_number:", newExpense.expense_number);
            console.log("Available suppliers:", suppliers);

            const supplier = newExpense.supplier ? suppliers.find(s => s.supplier_display_name === newExpense.supplier) : null;

            if (!newExpense.expense_number || !newExpense.expense_number.trim()) {
                throw new Error("Expense number is required. Please enter a reference number.");
            }

            const validItems = newExpense.items.map(item => ({
                rowNumber: item.rowNumber || "1",
                category: item.category || "General",
                description: item.description || "",
                totalAmount: item.totalAmount || "",
            }));

            const totalAmount = parseFloat(validItems.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0).toFixed(2));
            if (isNaN(totalAmount) || totalAmount <= 0) {
                throw new Error("Total amount must be a positive number.");
            }

            const formData = new FormData();
            formData.append("date", newExpense.date || getCurrentDate());
            formData.append("type", newExpense.type || "Expense");
            formData.append("expense_number", newExpense.expense_number.trim());
            formData.append("no", newExpense.expense_number.trim());
            if (supplier && !isNaN(supplier.id)) {
                formData.append("supplier", Number(supplier.id));
            }
            formData.append("method", newExpense.method || "Cash");
            formData.append("terms", newExpense.terms || "General");
            formData.append("memo", newExpense.memo || "");
            formData.append("amount", totalAmount.toFixed(2));
            formData.append("total", totalAmount.toFixed(2));
            formData.append("sales_tax", newExpense.sales_tax || "0.00");
            formData.append("status", "Paid");
            formData.append("class", newExpense.class || `Class-${Math.floor(Math.random() * 100)}`);
            formData.append("location", newExpense.location || `Location-${Math.floor(Math.random() * 100)}`);
            formData.append("source", newExpense.source || "Manual");
            formData.append("dueDate", newExpense.dueDate || getCurrentDate());
            formData.append("billApproval", newExpense.billApproval || "No");
            formData.append("items", JSON.stringify(validItems));

            if (expenseFile && (!selectedExpense || expenseFile.name !== selectedExpense.file_name)) {
                formData.append("file", expenseFile);
            }

            console.log("FormData for Save and New:", Object.fromEntries(formData));
            const response = await axios.post(`${API_BASE_URL}/api/expenses/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Accept': 'application/json',
                },
            });

            console.log("Created expense:", response.data);
            const createdExpense = response.data;
            const itemsTotal = createdExpense.items ? createdExpense.items.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0) : totalAmount;
            const total = parseFloat(createdExpense.total?.replace('£', '')) || itemsTotal;
            const displayCategory = categoryMap[createdExpense.category] || createdExpense.category || "General";

            const updatedExpense = {
                id: Number(createdExpense.id),
                date: formatDateToDDMMYYYY(createdExpense.date) || getCurrentDate(),
                type: createdExpense.type || "Expense",
                number: createdExpense.no || createdExpense.expense_number || newExpense.expense_number,
                payee: createdExpense.supplier || createdExpense.payee || newExpense.supplier || "Unknown",
                category: displayCategory,
                memo: createdExpense.memo || "",
                balance: createdExpense.totalBeforeTax || `£${itemsTotal.toFixed(2)}`,
                totalBalanceSalesTax: createdExpense.total || `£${total.toFixed(2)}`,
                salesTax: createdExpense.salesTax || "£0.00",
                total: `£${total.toFixed(2)}`,
                attachments: createdExpense.file_path ? createdExpense.file_path.split('/').pop() : "None",
                action: "View/Edit",
                address: createdExpense.address || "No address",
                items: createdExpense.items ? createdExpense.items.map(item => ({
                    id: Date.now() + Math.random(),
                    rowNumber: item.rowNumber || "1",
                    category: categoryMap[item.category] || item.category || "",
                    description: item.description || "",
                    totalAmount: item.totalAmount ? String(item.totalAmount) : "",
                })) : [
                    {
                        id: Date.now(),
                        rowNumber: "1",
                        category: displayCategory,
                        description: createdExpense.memo || "",
                        totalAmount: total ? String(total) : "",
                    },
                ],
                file_path: createdExpense.file_path || "",
                file_name: createdExpense.file_path ? createdExpense.file_path.split('/').pop() : "None",
                class: createdExpense.class || `Class-${Math.floor(Math.random() * 100)}`,
                location: createdExpense.location || `Location-${Math.floor(Math.random() * 100)}`,
                status: "Paid",
                method: createdExpense.method || ["Cash", "Card", "Bank Transfer"][Math.floor(Math.random() * 3)],
                source: createdExpense.source || ["Manual", "Imported"][Math.floor(Math.random() * 2)],
                dueDate: formatDateToDDMMYYYY(createdExpense.dueDate) || getCurrentDate(),
                billApproval: createdExpense.billApproval || ["Yes", "No"][Math.floor(Math.random() * 2)],
            };

            setExpenses((prev) => {
                const newExpenses = [...prev, updatedExpense];
                applyFilters(newExpenses);
                return newExpenses;
            });

            setPayee('');
            setPaymentDate(getCurrentDate());
            setPaymentMethod('');
            setDescription('');
            setCategory('');
            setExpenseFile(null);
            setTableData([
                {
                    id: Date.now(),
                    rowNumber: "1",
                    category: "",
                    description: "",
                    totalAmount: "",
                },
            ]);
            setShowCreateExpensePopup(true);
            setError(null);
            alert("Expense created successfully!");
        } catch (error) {
            console.error("Error saving expense (Save and New):", error.response ? error.response.data : error.message);
            setError(`Failed to save expense: ${error.response ? (typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data, null, 2)) : error.message}`);
        }
    };

    const handleExpenseCreated = async (newExpense) => {
        try {
            console.log("Handle Expense Created - newExpense:", newExpense);
            console.log("Manual expense_number:", newExpense.expense_number);
            console.log("Available suppliers:", suppliers);

            const supplier = newExpense.supplier ? suppliers.find(s => s.supplier_display_name === newExpense.supplier) : null;

            if (!newExpense.expense_number || !newExpense.expense_number.trim()) {
                throw new Error("Expense number is required. Please enter a reference number.");
            }

            const validItems = newExpense.items.map(item => ({
                rowNumber: item.rowNumber || "1",
                category: item.category || "General",
                description: item.description || "",
                totalAmount: item.totalAmount || "",
            }));

            const totalAmount = parseFloat(validItems.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0).toFixed(2));
            if (isNaN(totalAmount) || totalAmount <= 0) {
                throw new Error("Total amount must be a positive number.");
            }

            const formData = new FormData();
            formData.append("date", newExpense.date || getCurrentDate());
            formData.append("type", newExpense.type || "Expense");
            formData.append("expense_number", newExpense.expense_number.trim());
            formData.append("no", newExpense.expense_number.trim());
            if (supplier && !isNaN(supplier.id)) {
                formData.append("supplier", Number(supplier.id));
            }
            formData.append("method", newExpense.method || "Cash");
            formData.append("terms", newExpense.terms || "General");
            formData.append("memo", newExpense.memo || "");
            formData.append("amount", totalAmount.toFixed(2));
            formData.append("total", totalAmount.toFixed(2));
            formData.append("sales_tax", newExpense.sales_tax || "0.00");
            formData.append("status", "Paid");
            formData.append("class", newExpense.class || `Class-${Math.floor(Math.random() * 100)}`);
            formData.append("location", newExpense.location || `Location-${Math.floor(Math.random() * 100)}`);
            formData.append("source", newExpense.source || "Manual");
            formData.append("dueDate", newExpense.dueDate || getCurrentDate());
            formData.append("billApproval", newExpense.billApproval || "No");
            formData.append("items", JSON.stringify(validItems));

            if (expenseFile && (!selectedExpense || expenseFile.name !== selectedExpense.file_name)) {
                formData.append("file", expenseFile);
            }

            console.log("FormData Contents:", Object.fromEntries(formData));
            console.log("Expense ID for request:", selectedExpense ? selectedExpense.id : "New expense");

            let response;
            if (selectedExpense) {
                if (isNaN(selectedExpense.id)) {
                    console.error("Invalid ID in PUT request:", selectedExpense.id);
                    alert("Error: Invalid expense ID. Please try again.");
                    return;
                }
                response = await axios.put(`${API_BASE_URL}/api/expenses/${Number(selectedExpense.id)}/`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Accept': 'application/json',
                    },
                });
            } else {
                response = await axios.post(`${API_BASE_URL}/api/expenses/`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Accept': 'application/json',
                    },
                });
            }

            console.log("Created/Updated expense:", response.data);
            const createdExpense = response.data;
            const itemsTotal = createdExpense.items ? createdExpense.items.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0) : totalAmount;
            const total = parseFloat(createdExpense.total?.replace('£', '')) || itemsTotal;
            const displayCategory = categoryMap[createdExpense.category] || createdExpense.category || "General";

            const updatedExpense = {
                id: Number(createdExpense.id),
                date: formatDateToDDMMYYYY(createdExpense.date) || getCurrentDate(),
                type: createdExpense.type || "Expense",
                number: createdExpense.no || createdExpense.expense_number || newExpense.expense_number,
                payee: createdExpense.supplier || createdExpense.payee || newExpense.supplier || "Unknown",
                category: displayCategory,
                memo: createdExpense.memo || "",
                balance: createdExpense.totalBeforeTax || `£${itemsTotal.toFixed(2)}`,
                totalBalanceSalesTax: createdExpense.total || `£${total.toFixed(2)}`,
                salesTax: createdExpense.salesTax || "£0.00",
                total: `£${total.toFixed(2)}`,
                attachments: createdExpense.file_path ? createdExpense.file_path.split('/').pop() : "None",
                action: "View/Edit",
                address: createdExpense.address || "No address",
                items: createdExpense.items ? createdExpense.items.map(item => ({
                    id: Date.now() + Math.random(),
                    rowNumber: item.rowNumber || "1",
                    category: categoryMap[item.category] || item.category || "",
                    description: item.description || "",
                    totalAmount: item.totalAmount ? String(item.totalAmount) : "",
                })) : [
                    {
                        id: Date.now(),
                        rowNumber: "1",
                        category: displayCategory,
                        description: createdExpense.memo || "",
                        totalAmount: total ? String(total) : "",
                    },
                ],
                file_path: createdExpense.file_path || "",
                file_name: createdExpense.file_path ? createdExpense.file_path.split('/').pop() : "None",
                class: createdExpense.class || `Class-${Math.floor(Math.random() * 100)}`,
                location: createdExpense.location || `Location-${Math.floor(Math.random() * 100)}`,
                status: "Paid",
                method: createdExpense.method || ["Cash", "Card", "Bank Transfer"][Math.floor(Math.random() * 3)],
                source: createdExpense.source || ["Manual", "Imported"][Math.floor(Math.random() * 2)],
                dueDate: formatDateToDDMMYYYY(createdExpense.dueDate) || getCurrentDate(),
                billApproval: createdExpense.billApproval || ["Yes", "No"][Math.floor(Math.random() * 2)],
            };

            setExpenses((prev) => {
                const newExpenses = selectedExpense
                    ? prev.map((expense) => (expense.id === updatedExpense.id ? updatedExpense : expense))
                    : [...prev, updatedExpense];
                applyFilters(newExpenses);
                return newExpenses;
            });

            setShowCreateExpensePopup(false);
            setSelectedExpense(null);
            setPayee('');
            setPaymentDate(getCurrentDate());
            setPaymentMethod('');
            setDescription('');
            setCategory('');
            setExpenseFile(null);
            setTableData([
                {
                    id: Date.now(),
                    rowNumber: "1",
                    category: "",
                    description: "",
                    totalAmount: "",
                },
            ]);
            setError(null);
            alert(selectedExpense ? "Expense updated successfully!" : "Expense created successfully!");
        } catch (error) {
            console.error("Error saving expense:", error.response ? error.response.data : error.message);
            setError(`Failed to save expense: ${error.response ? (typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data, null, 2)) : error.message}`);
        }
    };

    const handleSupplierChange = (e) => {
        e.stopPropagation();
        setSupplierFilter(e.target.value);
    };

    const handleExpenseDateChange = (e) => {
        e.stopPropagation();
        setExpenseDateFilter(e.target.value);
    };

    const handleFromDateChange = (e) => {
        e.stopPropagation();
        setFromDate(e.target.value);
    };

    const handleToDateChange = (e) => {
        e.stopPropagation();
        setToDate(e.target.value);
    };

    const handleResetClick = (e) => {
        e.stopPropagation();
        resetFilters();
    };

    const handleApplyClick = (e) => {
        e.stopPropagation();
        applyFilters();
    };

    const applyFilters = (expensesToFilter = expenses) => {
        let filtered = [...expensesToFilter];

        if (supplierFilter !== "All") {
            filtered = filtered.filter((expense) => expense.payee === supplierFilter);
        }

        const today = new Date();
        const getExpenseDate = (expense) => {
            return parseDDMMYYYYToDate(expense.date);
        };

        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const firstDayOfQuarter = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastQuarter = new Date(today.getFullYear(), Math.floor((today.getMonth() - 3) / 3) * 3, 1);
        const lastYear = new Date(today.getFullYear() - 1, 0, 1);

        switch (expenseDateFilter) {
            case "Since 365 Days ago":
                const oneYearAgo = new Date();
                oneYearAgo.setDate(today.getDate() - 365);
                filtered = filtered.filter((expense) => getExpenseDate(expense) >= oneYearAgo);
                break;
            case "This Month":
                filtered = filtered.filter((expense) => getExpenseDate(expense) >= firstDayOfMonth);
                break;
            case "This Quarter":
                filtered = filtered.filter((expense) => getExpenseDate(expense) >= firstDayOfQuarter);
                break;
            case "This Year":
                filtered = filtered.filter((expense) => getExpenseDate(expense) >= firstDayOfYear);
                break;
            case "Last Month":
                filtered = filtered.filter((expense) => {
                    const expenseDate = getExpenseDate(expense);
                    return (
                        expenseDate.getFullYear() === lastMonth.getFullYear() &&
                        expenseDate.getMonth() === lastMonth.getMonth()
                    );
                });
                break;
            case "Last Quarter":
                filtered = filtered.filter((expense) => {
                    const expenseDate = getExpenseDate(expense);
                    return (
                        expenseDate >= lastQuarter &&
                        expenseDate < firstDayOfQuarter
                    );
                });
                break;
            case "Last Year":
                filtered = filtered.filter((expense) => {
                    const expenseDate = getExpenseDate(expense);
                    return expenseDate.getFullYear() === lastYear.getFullYear();
                });
                break;
            case "Custom":
                if (fromDate && toDate) {
                    const from = parseDDMMYYYYToDate(fromDate);
                    const to = parseDDMMYYYYToDate(toDate);
                    filtered = filtered.filter((expense) => {
                        const expenseDate = getExpenseDate(expense);
                        return expenseDate >= from && expenseDate <= to;
                    });
                }
                break;
            default:
                // No filtering for "All Dates"
        }

        setFilteredExpenses(filtered);
        setShowFilterPopup(false);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSupplierFilter("All");
        setExpenseDateFilter("All Dates");
        setFromDate("");
        setToDate("");
        setFilteredExpenses(expenses);
        setCurrentPage(1);
    };

    const handleSettingsChange = (newSettings) => {
        setSettings(newSettings);
        setCurrentPage(1);
    };

    const getVisibleColumns = () => {
        if (settings.showAllTransactions) {
            return [
                "date", "type", "number", "payee", "category", "class", "location",
                "status", "method", "source", "memo", "dueDate", "balance",
                "totalBalanceSalesTax", "salesTax", "total", "attachments",
                "billApproval", "action"
            ];
        }
        const columns = [];
        if (settings.showDate) columns.push("date");
        if (settings.showType) columns.push("type");
        if (settings.showNumber) columns.push("number");
        if (settings.showPayee) columns.push("payee");
        if (settings.showCategory) columns.push("category");
        if (settings.showClass) columns.push("class");
        if (settings.showLocation) columns.push("location");
        if (settings.showStatus) columns.push("status");
        if (settings.showMethod) columns.push("method");
        if (settings.showSource) columns.push("source");
        if (settings.showMemo) columns.push("memo");
        if (settings.showDueDate) columns.push("dueDate");
        if (settings.showBalance) columns.push("balance");
        if (settings.showTotalBalanceSalesTax) columns.push("totalBalanceSalesTax");
        if (settings.showSalesTax) columns.push("salesTax");
        if (settings.showTotal) columns.push("total");
        if (settings.showAttachments) columns.push("attachments");
        if (settings.showBillApproval) columns.push("billApproval");
        if (settings.showAction) columns.push("action");
        return columns;
    };

    const handleCheckboxChange = (index) => (e) => {
        e.stopPropagation();
        const checked = e.target.checked;
        setSelectedRows((prev) =>
            checked ? [...prev, index] : prev.filter((i) => i !== index)
        );
    };

    const handleHeaderCheckboxChange = (e) => {
        const checked = e.target.checked;
        setSelectedRows(checked ? expenses.map((_, index) => index) : []);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.pdf')) {
            if (file.size > 20 * 1024 * 1024) {
                alert("File size exceeds 20MB limit.");
                return;
            }
            setExpenseFile(file);
        } else {
            alert("Please select a PDF file.");
        }
    };

    const itemsPerPage = settings.rowLimit;
    const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentExpenses = filteredExpenses.slice(startIndex, endIndex);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        for (let i = 1; i <= totalPages; i++) {
            pageNumbers.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={currentPage === i ? 'active-page' : ''}
                    style={{
                        margin: '0 5px',
                        padding: '5px 10px',
                        cursor: 'pointer',
                        backgroundColor: currentPage === i ? '#007bff' : '#fff',
                        color: currentPage === i ? '#fff' : '#007bff',
                        border: '1px solid #007bff',
                        borderRadius: '3px',
                    }}
                >
                    {i}
                </button>
            );
        }
        return pageNumbers;
    };

    return (
        <div className="expenses-container">
            {error && <div className="error-message">Error: {error}</div>}
            {loadingSuppliers && <div>Loading suppliers...</div>}
            {loadingExpenses && <div>Loading expenses...</div>}
            <h1>Expenses</h1>
            <nav className="nav-bar">
                <NavLink
                    to="/admin/pk-expenses"
                    className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}
                >
                    Expenses
                </NavLink>
                <NavLink
                    to="/admin/bills"
                    className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}
                >
                    Bills
                </NavLink>
                <NavLink
                    to="/admin/pk-suppliers"
                    className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}
                >
                    Suppliers
                </NavLink>
            </nav>

            <div className="expenses-header-row" style={{ backgroundColor: '#f8f8f8', padding: '25px', marginTop: '-25px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <p className="expenses-title" style={{ marginLeft: '1px', marginBottom: '-10px', fontSize: '35px' }}>Expenses</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <NavLink to="/admin/feedback" className="header-btn">
                            <MdFeedback /> Give Feedback
                        </NavLink>
                        <div
                            className="dropdown-btn"
                            onClick={() => setShowPayDropdown(!showPayDropdown)}
                        >
                            Pay bills <FiChevronDown />
                            {showPayDropdown && (
                                <div ref={payDropdownRef} className="dropdown-meu" style={{ width: '220px' }}>
                                    <button>Make Recurring Payment</button>
                                </div>
                            )}
                        </div>
                        <div
                            className="dropdown-button"
                            onClick={() => setShowAddDropdown(!showAddDropdown)}
                        >
                            New Transaction <FiChevronDown />
                            {showAddDropdown && (
                                <div ref={addDropdownRef} className="dropdown-meu" style={{ width: '200px', height: '363px' }}>
                                    <button onClick={handleCreateExpenseClick}>Expense</button>
                                    <button>Bill</button>
                                    <button>Cheque</button>
                                    <button>Purchase Order</button>
                                    <button>Supplier Credit</button>
                                    <button>Credit Card Credit</button>
                                    <button>Pay Down Credit Card</button>
                                    <button>Import Bills</button>
                                    <button>Supplier refund</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="main-layout">
                <main className="contents">
                    <div className="filters">
                        <div className="filters-left">
                            <select>
                                <option>All transactions</option>
                                <option>Expense</option>
                                <option>Bill</option>
                                <option>Bill Payment</option>
                                <option>Cheque</option>
                                <option>Purchase Order</option>
                                <option>Recently Paid</option>
                                <option>Supplier Credit</option>
                            </select>

                            <div className="dropdowns-btn" ref={filterButtonRef} onClick={() => setShowFilterPopup(!showFilterPopup)}>
                                <button className='fills' style={{backgroundColor:"transparent"}}><LuSettings2 />Filter</button>
                                {showFilterPopup && (
                                    <div ref={filterPopupRef} className="dropdown-menu" style={{ width: "400px", padding: "1rem" }}>
                                        <div onClick={(e) => e.stopPropagation()}>
                                            <label>Supplier</label>
                                            <select value={supplierFilter} onChange={handleSupplierChange}>
                                                <option value="All">All</option>
                                                {suppliers.map((supplier) => (
                                                    <option key={supplier.id} value={supplier.supplier_display_name}>
                                                        {supplier.supplier_display_name}
                                                    </option>
                                                ))}
                                            </select>
                                            <label>Expense date</label>
                                            <div className="date-container">
                                                <select value={expenseDateFilter} onChange={handleExpenseDateChange}>
                                                    <option value="All Dates">All Dates</option>
                                                    <option value="Custom">Custom</option>
                                                    <option value="Since 365 Days ago">Since 365 Days ago</option>
                                                    <option value="This Month">This Month</option>
                                                    <option value="This Quarter">This Quarter</option>
                                                    <option value="This Year">This Year</option>
                                                    <option value="Last Month">Last Month</option>
                                                    <option value="Last Quarter">Last Quarter</option>
                                                    <option value="Last Year">Last Year</option>
                                                </select>
                                            </div>
                                            {expenseDateFilter === "Custom" && (
                                                <>
                                                    <input
                                                        type="date"
                                                        value={fromDate}
                                                        onChange={handleFromDateChange}
                                                        style={{ marginRight: "0.5rem" }}
                                                    />
                                                    <span style={{ marginRight: "0.5rem" }}>to</span>
                                                    <input
                                                        type="date"
                                                        value={toDate}
                                                        onChange={handleToDateChange}
                                                    />
                                                </>
                                            )}
                                            <div className="buttons" style={{ display: "flex", justifyContent: "space-between", marginTop: "1rem", gap: "1rem" }}>
                                                <button className="reset-btn" onClick={handleResetClick}>Reset</button>
                                                <button className="apply-btn" onClick={handleApplyClick}>Apply</button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <span className='date'>Dates: {expenseDateFilter === "Custom" ? "Custom" : expenseDateFilter}</span>
                        </div>
                        <div className="filters-right">
                            <button className='icons'><IoIosLogOut /></button>
                            <button className='icons' onClick={handlePrint}><IoMdPrint /></button>
                            <button
                                ref={settingsButtonRef}
                                className='icons'
                                onClick={() => setShowSettingsPopup(true)}
                            >
                                <CiSettings />
                            </button>
                        </div>
                    </div>

                    <table className="expenses-table">
                        <thead>
                            <tr>
                                <th style={{ width: '40px' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedRows.length === currentExpenses.length}
                                        onChange={handleHeaderCheckboxChange}
                                    />
                                </th>
                                {getVisibleColumns().map((column) => (
                                    <th key={column}>
                                        {column === "number" && "Number"}
                                        {column === "dueDate" && "Due Date"}
                                        {column === "totalBalanceSalesTax" && "Total w/ Sales Tax"}
                                        {column === "salesTax" && "Sales Tax"}
                                        {column === "billApproval" && "Bill Approval"}
                                        {column !== "number" && column !== "dueDate" && column !== "totalBalanceSalesTax" && column !== "salesTax" && column !== "billApproval" && column.charAt(0).toUpperCase() + column.slice(1)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {currentExpenses.map((item, index) => (
                                <tr
                                    key={item.id}
                                    className="clickable-row"
                                    onClick={(e) => handleRowClick(e, item)}
                                >
                                    <td style={{ width: '40px', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedRows.includes(index)}
                                            onChange={handleCheckboxChange(index)}
                                        />
                                    </td>
                                    {getVisibleColumns().map((column) => (
                                        <td key={column}>
                                            {column === "date" && item.date}
                                            {column === "type" && item.type}
                                            {column === "number" && item.number}
                                            {column === "payee" && item.payee}
                                            {column === "category" && item.category}
                                            {column === "class" && item.class}
                                            {column === "location" && item.location}
                                            {column === "status" && item.status}
                                            {column === "method" && item.method}
                                            {column === "source" && item.source}
                                            {column === "memo" && item.memo}
                                            {column === "dueDate" && item.dueDate}
                                            {column === "balance" && item.balance}
                                            {column === "totalBalanceSalesTax" && item.totalBalanceSalesTax}
                                            {column === "salesTax" && item.salesTax}
                                            {column === "total" && item.total}
                                            {column === "attachments" && item.attachments}
                                            {column === "billApproval" && item.billApproval}
                                            {column === "action" && (
                                                <div className="action-icons">
                                                    <button
                                                        className="action-icon edit-icon"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewEditExpense(item);
                                                        }}
                                                        title="View/Edit"
                                                    >
                                                        <FiEdit />
                                                    </button>
                                                    <button
                                                        className="action-icon delete-icon"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteExpense(item.id, item.number);
                                                        }}
                                                        title="Delete"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="pagination" style={{ marginTop: '20px', textAlign: 'center' }}>
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{
                                margin: '0 10px',
                                padding: '5px 10px',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                backgroundColor: currentPage === 1 ? '#ccc' : '#5e87b3ff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '3px',
                            }}
                        >
                            Previous
                        </button>
                        {renderPageNumbers()}
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{
                                margin: '0 10px',
                                padding: '5px 10px',
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                backgroundColor: currentPage === totalPages ? '#ccc' : '#5e87b3ff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '3px',
                            }}
                        >
                            Next
                        </button>
                    </div>
                </main>
            </div>

            {showSettingsPopup && (
                <div ref={settingsPopupRef}>
                    <SettingsPopup
                        settings={settings}
                        onSettingsChange={handleSettingsChange}
                        onClose={() => setShowSettingsPopup(false)}
                        anchorRef={settingsButtonRef}
                    />
                </div>
            )}
            <CreateExpensePopup
                showCreateExpensePopup={showCreateExpensePopup}
                setShowCreateExpensePopup={setShowCreateExpensePopup}
                suppliers={suppliers}
                payee={payee}
                setPayee={setPayee}
                paymentAccount={paymentAccount}
                setPaymentAccount={setPaymentAccount}
                paymentDate={paymentDate}
                setPaymentDate={setPaymentDate}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                description={description}
                setDescription={setDescription}
                expenseFile={expenseFile}
                setExpenseFile={setExpenseFile}
                tableData={tableData}
                setTableData={setTableData}
                totalAmount={totalAmount}
                handleExpenseCreated={handleExpenseCreated}
                handleSaveAndNew={handleSaveAndNew}
                handleClear={handleClear}
                handlePrint={handlePrint}
                handleRecurring={handleRecurring}
                handleAddRow={handleAddRow}
                handleDeleteRow={handleDeleteRow}
                handleCopyRow={handleCopyRow}
                handleUpdateRow={handleUpdateRow}
                handleClearAllLines={handleClearAllLines}
                handleFileChange={handleFileChange}
                selectedExpense={selectedExpense}
                categories={categories}
            />
        </div>
    );
};

export default ExpensesPage;